import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { zplToLayout } from "@/controls/tag-label";
import {
	cancelPrintQueueItem,
	getPrintQueueItem,
	getPrintQueue,
	getPrinters,
	printTestTag,
	reprintPrintQueueItem,
	upsertPrinter,
} from "../printersRepository";

const FINAL = ["Printed", "Failed", "Cancelled", "Expired"];
const SENT_GRACE_MS = 15_000;

export const usePrinters = (onError: (e: Error) => void) =>
	useQuery({
		queryKey: ["printers"],
		queryFn: getPrinters,
		refetchInterval: 5_000,
		onError,
	});

export const useSavePrinter = (
	onDone: () => void,
	onError: (e: Error) => void,
) => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: upsertPrinter,
		onSuccess: async () => {
			await qc.invalidateQueries({ queryKey: ["printers"] });
			onDone();
		},
		onError,
	});
};

const useAfterPrint = () => {
	const qc = useQueryClient();
	return () => {
		void qc.invalidateQueries({ queryKey: ["printers"] });
		void qc.invalidateQueries({ queryKey: ["printQueue"] });
	};
};

export const useTestPrint = (
	onDone: (printQueueItemId: number) => void,
	onError: (e: Error) => void,
) => {
	const after = useAfterPrint();
	return useMutation({
		mutationFn: printTestTag,
		onSuccess: (r) => {
			after();
			onDone(r.printQueueItemId);
		},
		onError,
	});
};

export const useReprint = (
	onDone: (printQueueItemId: number) => void,
	onError: (e: Error) => void,
) => {
	const after = useAfterPrint();
	return useMutation({
		mutationFn: reprintPrintQueueItem,
		onSuccess: (r) => {
			after();
			onDone(r.printQueueItemId);
		},
		onError,
	});
};

export const useCancelPrint = (onError: (e: Error) => void) => {
	const after = useAfterPrint();
	return useMutation({
		mutationFn: cancelPrintQueueItem,
		onSuccess: after,
		onError,
	});
};

export const usePrintQueue = (
	printerId: number,
	enabled: boolean,
	onError: (e: Error) => void,
) =>
	useQuery({
		queryKey: ["printQueue", printerId],
		queryFn: () => getPrintQueue(printerId),
		enabled,
		refetchInterval: 3_000,
		onError,
	});

export const usePrintQueueItem = (
	printQueueItemId: number | null,
	onError: (e: Error) => void,
) => {
	const [unconfirmed, setUnconfirmed] = useState(false);
	const query = useQuery({
		queryKey: ["printQueueItem", printQueueItemId],
		queryFn: () => getPrintQueueItem(printQueueItemId as number),
		enabled: printQueueItemId != null,
		refetchInterval: (d) =>
			FINAL.includes(d?.item?.Status ?? "") || unconfirmed ? false : 1000,
		onError,
	});
	const status = query.data?.item?.Status;
	const after = useAfterPrint();
	// biome-ignore lint/correctness/useExhaustiveDependencies: on final status only
	useEffect(() => {
		if (FINAL.includes(status ?? "")) after();
	}, [status]);
	useEffect(() => {
		setUnconfirmed(false);
		if (status !== "Sent") return;
		const t = setTimeout(() => setUnconfirmed(true), SENT_GRACE_MS);
		return () => clearTimeout(t);
	}, [status, printQueueItemId]);
	return { ...query, unconfirmed };
};

// an item's commands never change
export const useTagLayout = (
	printQueueItemId: number | null,
	commands: string | null | undefined,
	onError: (e: Error) => void,
) =>
	useQuery({
		queryKey: ["tagLayout", printQueueItemId],
		queryFn: () => zplToLayout(commands as string),
		enabled: printQueueItemId != null && !!commands,
		staleTime: Number.POSITIVE_INFINITY,
		onError,
	});
